import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { getAdminMediaFiles } from 'ServerAPI'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import Select from 'components/base/Select'
import MenuItem from 'components/base/MenuItem'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { formatUnixTime } from '~/components/admin/formatUnixTime'
import { usePageSize } from '~/components/admin/usePageSize'
import { getTruncatedValue } from '~/components/admin/ValuePopup'
import { AppRoutes } from '~/AppRoutes'
import './RecentMediaFiles.scss'

const searchByOptions = [
  'fileId',
  'name',
  'userId',
  'status',
  'mediaType',
  'fileType',
  'width',
  'height',
  'codecsInfo',
]

const getQueryState = locationSearch => {
  const queryParams = new URLSearchParams(locationSearch)
  const sortByFromQuery = queryParams.get('sortBy') || queryParams.get('searchBy')
  return {
    search: queryParams.get('search') || '',
    searchBy: searchByOptions.includes(sortByFromQuery) ? sortByFromQuery : 'name',
  }
}

const getSearchUrl = (searchBy, search) => {
  const queryParams = new URLSearchParams()
  queryParams.set('sortBy', searchBy)
  queryParams.set('search', String(search))
  return `${AppRoutes.AdminRecentMediaFiles}?${queryParams.toString()}`
}

const getSearchValue = (value, searchBy) => {
  if (value === null || value === undefined || value === '') return value
  return (
    <Link
      className="admin-panel__link"
      to={getSearchUrl(searchBy, value)}
    >
      {String(value)}
    </Link>
  )
}

const getOriginalUrlValue = (value, originalUrl) => {
  if (!originalUrl) return value
  return (
    <a
      className="admin-panel__link"
      href={originalUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {String(value)}
    </a>
  )
}

const getNameValue = name => {
  if (name === null || name === undefined) return name
  return (
    <span className="admin-list__table__cell-text" title={String(name)}>
      {getTruncatedValue(name)}
    </span>
  )
}

export const RecentMediaFiles = () => {
  const { pageSize, tableRef, isReady } = usePageSize()
  const location = useLocation()
  const initialQueryState = getQueryState(location.search)
  const [ searchText, setSearchText ] = useState(initialQueryState.search)
  const [ search, setSearch ] = useState(initialQueryState.search)
  const [ searchBy, setSearchBy ] = useState(initialQueryState.searchBy)
  const [ start, setStart ] = useState(0)
  const [ mediaFiles, setMediaFiles ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ loading, setLoading ] = useState(false)

  useEffect(() => {
    const nextQueryState = getQueryState(location.search)
    setStart(0)
    setSearchText(nextQueryState.search)
    setSearch(nextQueryState.search)
    setSearchBy(nextQueryState.searchBy)
  }, [ location.search ])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminMediaFiles({
        start_number: start,
        count: pageSize,
        search,
        searchBy,
      })
      setMediaFiles(data.entries || [])
      setTotalCount(data.total_count || 0)
    } catch {
      setMediaFiles([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [ start, search, searchBy, pageSize ])

  useEffect(() => {
    if (!isReady) return
    load()
  }, [ load, isReady ])

  const onSearch = () => {
    setStart(0)
    setSearch(searchText)
  }

  const onPage = diff => {
    const nextStart = start + diff * pageSize
    if (nextStart < 0 || nextStart >= totalCount) return
    setStart(nextStart)
  }

  const currentPage = totalCount ? Math.floor(start / pageSize) + 1 : 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="admin-list">
      <div className="admin-list__search">
        <span className="admin-list__search__label">Search by</span>
        <Select
          className="admin-list__search__select admin-list__search__select--media"
          dropdownClassName="admin-list__search__dropdown--media"
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value)
            setStart(0)
          }}
        >
          {searchByOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
        <Input
          id="admin-media-files-search"
          className="admin-list__search__input"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onEnterKeyDown={onSearch}
        />
        <Button className="admin-list__search__button" primary onClick={onSearch}>
          Search
        </Button>
      </div>

      <div className="admin-list__table" ref={tableRef}>
        <Table
          className="admin-list__table__media"
          aria-label="recent media files"
        >
          <TableHead>
            <TableRow>
              <TableCell className="admin-list__table__file-id">fileId</TableCell>
              <TableCell className="admin-list__table__name">name</TableCell>
              <TableCell>userId</TableCell>
              <TableCell>status</TableCell>
              <TableCell>mediaType</TableCell>
              <TableCell>filetype</TableCell>
              <TableCell>duration(100ns_units)</TableCell>
              <TableCell>size(bytes)</TableCell>
              <TableCell>codecsInfo</TableCell>
              <TableCell>width</TableCell>
              <TableCell>height</TableCell>
              <TableCell>createTime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mediaFiles.map((file, index) => (
              <TableRow key={file.id || index}>
                <TableCell className="admin-list__table__file-id">
                  {getOriginalUrlValue(file.id, file.originalUrl)}
                </TableCell>
                <TableCell className="admin-list__table__name">
                  {getNameValue(file.name)}
                </TableCell>
                <TableCell>
                  {file.userId === null || file.userId === undefined || file.userId === ''
                    ? file.userId
                    : (
                      <Link
                        className="admin-panel__link"
                        to={`${AppRoutes.AdminRecentUsers}/${file.userId}`}
                      >
                        {file.userId}
                      </Link>
                    )}
                </TableCell>
                <TableCell>{getSearchValue(file.status, 'status')}</TableCell>
                <TableCell>{getSearchValue(file.mediaType, 'mediaType')}</TableCell>
                <TableCell>{getSearchValue(file.filetype, 'fileType')}</TableCell>
                <TableCell>{file.duration}</TableCell>
                <TableCell>{file.size}</TableCell>
                <TableCell>{getSearchValue(file.codecsInfo, 'codecsInfo')}</TableCell>
                <TableCell>{getSearchValue(file.width, 'width')}</TableCell>
                <TableCell>{getSearchValue(file.height, 'height')}</TableCell>
                <TableCell>{formatUnixTime(file.createTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="admin-list__footer">
        <If condition={loading}>
          <CircularProgress endless percents={80} transparent size={24} />
        </If>
        <div className="admin-list__footer__nav">
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(-1)}
            disabled={start === 0}
          >
            Prev
          </Button>
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(1)}
            disabled={totalCount === 0 || start + pageSize >= totalCount}
          >
            Next
          </Button>
        </div>
        <span className="admin-list__footer__info">
          {'page '}
          {String(currentPage)}
          {' of '}
          {String(totalPages)}
          {', totalCount: '}
          {String(totalCount)}
        </span>
      </div>
    </div>
  )
}
